import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Check, Pencil, Trash2, Bell, BellOff, Volume2, RotateCcw, List, LayoutGrid, Plus, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminNav } from "../components/admin/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 w-full rounded-md bg-seed-off px-4 text-sm text-seed-deep outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]";

const DEFAULT_TYPE_LABELS = {
  reuniao: "Reunião",
  ligacao: "Ligação",
  documentacao: "Documentação",
  visita: "Visita",
  outro: "Outro",
};
const DEFAULT_TYPES = Object.keys(DEFAULT_TYPE_LABELS);

const typeCls = (t) =>
  ({ reuniao: "bg-seed-deep text-seed-off", ligacao: "bg-seed-salad/15 text-seed-salad", documentacao: "bg-seed-vivid/25 text-seed-deep", visita: "bg-blue-100 text-blue-600" })[t] ||
  "bg-seed-off text-seed-deep/60";
const typeLabel = (t) => DEFAULT_TYPE_LABELS[t] || t;

const REMINDERS = [
  { value: 0, label: "No horário" },
  { value: 5, label: "5 min antes" },
  { value: 15, label: "15 min antes" },
  { value: 30, label: "30 min antes" },
  { value: 60, label: "1h antes" },
];

export const urlB64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

export const playAlarm = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    [0, 0.45, 0.9].forEach((t) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.frequency.value = 880;
      o.type = "sine";
      g.gain.setValueAtTime(0.001, ctx.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.5, ctx.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + 0.35);
      o.start(ctx.currentTime + t);
      o.stop(ctx.currentTime + t + 0.4);
    });
  } catch {
    /* navegador bloqueou o áudio */
  }
};

const fmtDay = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" }) + " às " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const emptyForm = { title: "", type: "reuniao", date: "", assignee: "", remind_minutes: 15, description: "" };

const DEFAULT_COLUMNS = [
  { id: "pendente", label: "Pendentes", built_in: true },
  { id: "em_andamento", label: "Em andamento", built_in: true },
  { id: "concluido", label: "Concluídos", built_in: true },
];

const apptStatus = (a) => a.status || (a.done ? "concluido" : "pendente");
const isOverdue = (a) => apptStatus(a) !== "concluido" && new Date(a.date) < new Date();

export default function AdminAgendaEquipe() {
  const { user, headers, logout } = useAdminAuth();
  const [items, setItems] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pendente");
  const [search, setSearch] = useState("");
  const [types, setTypes] = useState(DEFAULT_TYPES);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [view, setView] = useState("lista");
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColumn, setNewColumn] = useState("");
  const [editingCol, setEditingCol] = useState(null);
  const [addingType, setAddingType] = useState(false);
  const [newType, setNewType] = useState("");
  const [manageTypes, setManageTypes] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const isAdmin = user?.role === "admin";
  const allowed = user?.role === "admin" || user?.role === "administrativo";

  const load = useCallback(async () => {
    try {
      const [a, u, tp, cl] = await Promise.all([
        axios.get(`${API}/agenda/appointments`, { headers: headers() }),
        axios.get(`${API}/agenda/assignees`, { headers: headers() }),
        axios.get(`${API}/agenda/types`, { headers: headers() }),
        axios.get(`${API}/agenda/columns`, { headers: headers() }),
      ]);
      setItems(a.data);
      setAssignees(u.data);
      setTypes(tp.data);
      setColumns(cl.data);
    } catch {
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addType = async () => {
    const name = newType.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/agenda/types`, { name }, { headers: headers() });
      toast.success(`Categoria "${name}" criada!`);
      setForm((f) => ({ ...f, type: name }));
      setNewType("");
      setAddingType(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível criar a categoria.");
    }
  };

  const removeType = async (name) => {
    if (!window.confirm(`Excluir a categoria "${name}"? Só é possível se nenhum compromisso usar.`)) return;
    try {
      await axios.delete(`${API}/agenda/types/${encodeURIComponent(name)}`, { headers: headers() });
      toast.success("Categoria excluída.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível excluir.");
    }
  };

  const moveCard = async (a, colId) => {
    if (apptStatus(a) === colId) return;
    const label = columns.find((c) => c.id === colId)?.label || colId;
    setItems((its) => (its || []).map((x) => (x.id === a.id ? { ...x, status: colId, done: colId === "concluido" } : x)));
    try {
      await axios.put(`${API}/agenda/appointments/${a.id}`, { status: colId }, { headers: headers() });
      toast.success(`"${a.title}" movido para ${label}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível mover.");
      await load();
    }
  };

  const addColumn = async () => {
    const label = newColumn.trim();
    if (!label) return;
    try {
      await axios.post(`${API}/agenda/columns`, { label }, { headers: headers() });
      toast.success(`Coluna "${label}" criada!`);
      setNewColumn("");
      setAddingColumn(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível criar a coluna.");
    }
  };

  const removeColumn = async (col) => {
    if (!window.confirm(`Excluir a coluna "${col.label}"? Só é possível se estiver vazia.`)) return;
    try {
      await axios.delete(`${API}/agenda/columns/${col.id}`, { headers: headers() });
      toast.success("Coluna excluída.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível excluir.");
    }
  };

  const renameColumn = async () => {
    const label = editingCol?.label?.trim();
    if (!label) return;
    try {
      await axios.put(`${API}/agenda/columns/${editingCol.id}`, { label }, { headers: headers() });
      toast.success("Coluna renomeada.");
      setEditingCol(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível renomear.");
    }
  };

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  useEffect(() => {
    if (!allowed) return;
    const supported = "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
    if (supported) {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setPushEnabled(!!sub))
        .catch(() => {});
    }
  }, [allowed]);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permita as notificações nas configurações do navegador.");
        return;
      }
      const { data } = await axios.get(`${API}/push/vapid-public`);
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(data.public_key) });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON() }, { headers: headers() });
      setPushEnabled(true);
      toast.success("Push ativado! Os lembretes chegam neste aparelho mesmo com a página fechada.");
    } catch {
      toast.error("Não foi possível ativar o push neste aparelho.");
    } finally {
      setPushBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      remind_minutes: Number(form.remind_minutes),
      assignee: isAdmin ? form.assignee : user.email,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/agenda/appointments/${editingId}`, payload, { headers: headers() });
        toast.success("Compromisso atualizado!");
        setEditingId(null);
      } else {
        await axios.post(`${API}/agenda/appointments`, payload, { headers: headers() });
        toast.success("Compromisso agendado!");
      }
      setForm(emptyForm);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title,
      type: a.type,
      date: a.date,
      assignee: a.assignee,
      remind_minutes: a.remind_minutes,
      description: a.description || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleDone = async (a) => {
    try {
      await axios.put(`${API}/agenda/appointments/${a.id}`, { done: !a.done }, { headers: headers() });
      await load();
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Excluir o compromisso "${a.title}"?`)) return;
    try {
      await axios.delete(`${API}/agenda/appointments/${a.id}`, { headers: headers() });
      toast.success("Compromisso excluído.");
      await load();
    } catch {
      toast.error("Não foi possível excluir.");
    }
  };

  const assigneeName = (email) => assignees.find((u) => u.email === email)?.name || email;

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTomorrow = new Date(startOfToday.getTime() + 86400000);
  const startOfAfter = new Date(startOfTomorrow.getTime() + 86400000);

  const q = search.trim().toLowerCase();
  const matchSearch = (a) => !q || `${a.title} ${a.description || ""} ${typeLabel(a.type)} ${assigneeName(a.assignee)}`.toLowerCase().includes(q);
  const visible = (items || [])
    .filter((a) => (statusFilter === "todos" ? true : apptStatus(a) === statusFilter))
    .filter(matchSearch);
  const groups = [
    { key: "hoje", label: "Hoje", items: visible.filter((a) => { const d = new Date(a.date); return d >= startOfToday && d < startOfTomorrow; }) },
    { key: "amanha", label: "Amanhã", items: visible.filter((a) => { const d = new Date(a.date); return d >= startOfTomorrow && d < startOfAfter; }) },
    { key: "proximos", label: "Próximos", items: visible.filter((a) => new Date(a.date) >= startOfAfter) },
    { key: "passados", label: "Anteriores", items: visible.filter((a) => new Date(a.date) < startOfToday) },
  ].filter((g) => g.items.length > 0);

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-6 pt-20 pb-24 lg:px-10" data-testid="admin-agenda-equipe">
      <AdminNav user={user} onLogout={logout} />
      <div className="mt-8 mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-seed-salad">Agenda da equipe</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-seed-deep" data-testid="agenda-equipe-title">
            Compromissos do administrativo
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={playAlarm}
            data-testid="agenda-test-sound"
            className="flex h-12 items-center gap-2 rounded-full border border-seed-deep/20 px-5 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-seed-salad hover:text-seed-salad"
          >
            <Volume2 size={15} /> Testar som
          </button>
          <button
            onClick={enablePush}
            disabled={pushBusy || pushEnabled}
            data-testid="agenda-push-toggle"
            className={`flex h-12 items-center gap-2 rounded-full px-5 font-heading text-sm font-bold transition-colors ${pushEnabled ? "bg-seed-salad/15 text-seed-salad" : "bg-seed-deep text-seed-off hover:scale-[1.03]"} disabled:opacity-70`}
          >
            {pushEnabled ? <><Bell size={15} /> Push ativado</> : pushBusy ? "Ativando..." : <><BellOff size={15} /> Ativar push no celular</>}
          </button>
        </div>
      </div>

      {!allowed ? (
        <p className="rounded-lg bg-white p-10 text-center font-semibold text-seed-deep/60 shadow-sm" data-testid="agenda-equipe-restricted">
          Acesso restrito à equipe administrativa.
        </p>
      ) : (
        <>
          <form onSubmit={save} className="rounded-lg bg-white p-8 shadow-sm" data-testid="appointment-form">
            <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-seed-deep">
              <CalendarClock size={18} className="text-seed-salad" /> {editingId ? "Editar compromisso" : "Novo compromisso"}
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <input required placeholder="Título (ex.: Reunião com contador)" value={form.title} onChange={set("title")} className={inputCls} data-testid="appt-title" />
              <div>
                <select value={form.type} onChange={set("type")} className={inputCls} data-testid="appt-type">
                  {types.map((t) => (
                    <option key={t} value={t}>{typeLabel(t)}</option>
                  ))}
                </select>
                <div className="mt-1 flex items-center gap-3">
                  {addingType ? (
                    <div className="flex w-full items-center gap-1.5">
                      <input
                        autoFocus
                        placeholder="Nome da nova categoria"
                        value={newType}
                        onChange={(e) => setNewType(e.target.value)}
                        className="h-9 flex-1 rounded-md bg-seed-off px-3 text-xs text-seed-deep outline-none focus:shadow-[0_0_0_2px_#00D765]"
                        data-testid="appt-new-type"
                      />
                      <button type="button" onClick={addType} className="rounded-full bg-seed-deep px-3 py-1.5 text-[10px] font-bold text-seed-off" data-testid="appt-new-type-save">OK</button>
                      <button type="button" onClick={() => { setAddingType(false); setNewType(""); }} className="text-[10px] font-bold text-seed-deep/50" data-testid="appt-new-type-cancel">X</button>
                    </div>
                  ) : (
                    <>
                      <button type="button" onClick={() => setAddingType(true)} className="text-[10px] font-bold text-seed-salad transition-colors hover:text-seed-deep" data-testid="appt-add-type">
                        + Nova categoria
                      </button>
                      <button type="button" onClick={() => setManageTypes((v) => !v)} className="text-[10px] font-bold text-seed-deep/40 transition-colors hover:text-seed-deep" data-testid="appt-manage-types">
                        Gerenciar
                      </button>
                    </>
                  )}
                </div>
                {manageTypes && !addingType && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="appt-types-manager">
                    {types.map((t) => (
                      <span key={t} className="flex items-center gap-1 rounded-full bg-seed-off px-2.5 py-1 text-[10px] font-bold text-seed-deep/70">
                        {typeLabel(t)}
                        {DEFAULT_TYPES.includes(t) ? (
                          <span className="text-seed-deep/30" title="Categoria padrão">·</span>
                        ) : (
                          <button type="button" onClick={() => removeType(t)} aria-label={`Excluir categoria ${t}`} className="text-seed-deep/40 transition-colors hover:text-red-500" data-testid={`appt-type-del-${t.replace(/\W+/g, "-")}`}>
                            <Trash2 size={10} />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <input required type="datetime-local" value={form.date} onChange={set("date")} className={inputCls} data-testid="appt-date" />
                <p className="mt-1 text-[10px] font-semibold text-seed-deep/40">Data e hora do compromisso</p>
              </div>
              {isAdmin ? (
                <select required value={form.assignee} onChange={set("assignee")} className={inputCls} data-testid="appt-assignee">
                  <option value="">Responsável...</option>
                  {assignees.map((u) => (
                    <option key={u.email} value={u.email}>{u.name || u.email}</option>
                  ))}
                </select>
              ) : (
                <div className="flex h-12 items-center rounded-md bg-seed-off px-4 text-sm font-semibold text-seed-deep/60" data-testid="appt-assignee-self">
                  Para: {user?.name || user?.email}
                </div>
              )}
              <select value={form.remind_minutes} onChange={set("remind_minutes")} className={inputCls} data-testid="appt-remind">
                {REMINDERS.map((r) => (
                  <option key={r.value} value={r.value}>Lembrete: {r.label}</option>
                ))}
              </select>
              <input placeholder="Descrição (opcional)" value={form.description} onChange={set("description")} className={inputCls} data-testid="appt-description" />
            </div>
            <div className="mt-5 flex gap-3">
              <button type="submit" disabled={saving} data-testid="appt-submit" className="rounded-full bg-seed-deep px-8 py-3.5 font-heading text-sm font-bold text-seed-off transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60">
                {saving ? "Salvando..." : editingId ? "Salvar alterações" : "Agendar"}
              </button>
              {editingId && (
                <button type="button" onClick={() => { setEditingId(null); setForm(emptyForm); }} data-testid="appt-cancel-edit" className="flex items-center gap-2 rounded-full border border-seed-deep/20 px-6 py-3.5 font-heading text-sm font-bold text-seed-deep transition-colors hover:border-red-400 hover:text-red-500">
                  <RotateCcw size={14} /> Cancelar edição
                </button>
              )}
            </div>
          </form>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs font-bold uppercase tracking-widest text-seed-deep/40" data-testid="appt-count">
              {visible.length} de {(items || []).length} compromisso(s)
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex gap-1.5" data-testid="appt-view-toggle">
                <button
                  onClick={() => setView("lista")}
                  data-testid="appt-view-lista"
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2.5 font-heading text-xs font-bold transition-colors ${view === "lista" ? "bg-seed-deep text-seed-off" : "bg-white text-seed-deep/60 shadow-sm hover:text-seed-salad"}`}
                >
                  <List size={13} /> Lista
                </button>
                <button
                  onClick={() => setView("kanban")}
                  data-testid="appt-view-kanban"
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2.5 font-heading text-xs font-bold transition-colors ${view === "kanban" ? "bg-seed-deep text-seed-off" : "bg-white text-seed-deep/60 shadow-sm hover:text-seed-salad"}`}
                >
                  <LayoutGrid size={13} /> Kanban
                </button>
              </div>
              <div className="relative">
                <input
                  placeholder="Buscar compromisso..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-10 w-52 rounded-full bg-white pl-4 pr-4 text-xs font-semibold text-seed-deep shadow-sm outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]"
                  data-testid="appt-search"
                />
              </div>
              {view === "lista" && (
                <div className="flex flex-wrap gap-1.5" data-testid="appt-status-filters">
                  {[...columns.map((c) => ({ key: c.id, label: c.label })), { key: "todos", label: "Todos" }].map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setStatusFilter(f.key)}
                      data-testid={`appt-filter-${f.key}`}
                      className={`rounded-full px-4 py-2.5 font-heading text-xs font-bold transition-colors ${statusFilter === f.key ? "bg-seed-deep text-seed-off" : "bg-white text-seed-deep/60 shadow-sm hover:text-seed-salad"}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {view === "lista" && (
          <div className="mt-4 space-y-6" data-testid="appt-list">
            {items === null ? (
              <div className="flex justify-center py-10">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
              </div>
            ) : groups.length === 0 ? (
              <p className="rounded-lg bg-white p-10 text-center text-seed-deep/50 shadow-sm" data-testid="appt-empty">Nenhum compromisso agendado.</p>
            ) : (
              groups.map((g) => (
                <div key={g.key}>
                  <p className="mb-2 text-xs font-bold uppercase tracking-widest text-seed-deep/50" data-testid={`appt-group-${g.key}`}>{g.label}</p>
                  <div className="space-y-2.5">
                    {g.items.map((a) => {
                      return (
                        <div key={a.id} className={`flex flex-wrap items-center gap-3 rounded-lg bg-white p-4 shadow-sm ${a.done ? "opacity-50" : ""}`} data-testid={`appt-item-${a.id.slice(0, 8)}`}>
                          <button
                            onClick={() => toggleDone(a)}
                            data-testid={`appt-done-${a.id.slice(0, 8)}`}
                            aria-label={a.done ? "Reabrir" : "Concluir"}
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${a.done ? "bg-seed-salad text-white" : "bg-seed-off text-seed-deep/30 hover:text-seed-salad"}`}
                          >
                            <Check size={15} />
                          </button>
                          <div className="min-w-0 flex-1">
                            <p className={`font-heading text-sm font-bold text-seed-deep ${a.done ? "line-through" : ""}`}>{a.title}</p>
                            <p className="mt-0.5 text-xs text-seed-deep/50">
                              {fmtDay(a.date)} · {assigneeName(a.assignee)}
                              {a.description ? ` · ${a.description}` : ""}
                            </p>
                          </div>
                          <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${typeCls(a.type)}`}>{typeLabel(a.type)}</span>
                          <span className="rounded-full bg-seed-off px-3 py-1 text-[10px] font-bold text-seed-deep/50">
                            {REMINDERS.find((r) => r.value === a.remind_minutes)?.label || "No horário"}
                          </span>
                          <button onClick={() => startEdit(a)} data-testid={`appt-edit-${a.id.slice(0, 8)}`} aria-label="Editar" className="text-seed-deep/40 transition-colors hover:text-seed-salad">
                            <Pencil size={15} />
                          </button>
                          <button onClick={() => remove(a)} data-testid={`appt-delete-${a.id.slice(0, 8)}`} aria-label="Excluir" className="text-seed-deep/40 transition-colors hover:text-red-500">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
          )}

          {view === "kanban" && (
            <div className="mt-4 flex items-start gap-3 overflow-x-auto pb-4" data-testid="kanban-board">
              {items === null ? (
                <div className="flex w-full justify-center py-10">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
                </div>
              ) : (
                <>
                  {columns.map((col, ci) => {
                    const colItems = (items || []).filter(matchSearch).filter((a) => apptStatus(a) === col.id);
                    return (
                      <div
                        key={col.id}
                        className="w-64 shrink-0 rounded-lg bg-seed-off/60 p-3"
                        data-testid={`kanban-col-${col.id}`}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const id = e.dataTransfer.getData("text/plain");
                          const card = (items || []).find((x) => x.id === id);
                          if (card) moveCard(card, col.id);
                        }}
                      >
                        <div className="mb-3 flex items-center justify-between gap-2">
                          {editingCol?.id === col.id ? (
                            <div className="flex w-full items-center gap-1.5">
                              <input
                                autoFocus
                                value={editingCol.label}
                                onChange={(e) => setEditingCol((c) => ({ ...c, label: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); renameColumn(); } if (e.key === "Escape") setEditingCol(null); }}
                                className="h-7 w-full rounded-md bg-white px-2 text-xs font-bold text-seed-deep outline-none focus:shadow-[0_0_0_2px_#00D765]"
                                data-testid={`kanban-col-input-${col.id}`}
                              />
                              <button onClick={renameColumn} aria-label="Salvar nome da coluna" className="shrink-0 text-seed-deep/40 transition-colors hover:text-seed-salad" data-testid={`kanban-col-save-${col.id}`}>
                                <Check size={13} />
                              </button>
                              <button onClick={() => setEditingCol(null)} aria-label="Cancelar" className="shrink-0 text-seed-deep/40 transition-colors hover:text-red-500">
                                <X size={13} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <p className="font-heading text-xs font-black uppercase tracking-widest text-seed-deep/70" data-testid={`kanban-col-title-${col.id}`}>
                                {col.label} <span className="text-seed-deep/30">({colItems.length})</span>
                              </p>
                              <div className="flex shrink-0 items-center gap-1">
                                <button onClick={() => setEditingCol({ id: col.id, label: col.label })} aria-label={`Renomear coluna ${col.label}`} title="Renomear coluna" className="text-seed-deep/30 transition-colors hover:text-seed-salad" data-testid={`kanban-col-edit-${col.id}`}>
                                  <Pencil size={11} />
                                </button>
                                <button onClick={() => removeColumn(col)} aria-label={`Excluir coluna ${col.label}`} title="Excluir coluna (só se estiver vazia)" className="text-seed-deep/30 transition-colors hover:text-red-500" data-testid={`kanban-col-del-${col.id}`}>
                                  <Trash2 size={11} />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="space-y-2">
                          {colItems.map((a) => (
                            <div
                              key={a.id}
                              draggable
                              onDragStart={(e) => e.dataTransfer.setData("text/plain", a.id)}
                              className={`cursor-grab rounded-md bg-white p-3 shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing ${apptStatus(a) === "concluido" ? "opacity-60" : ""}`}
                              data-testid={`kanban-card-${a.id.slice(0, 8)}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <button onClick={() => startEdit(a)} className={`flex-1 text-left text-xs font-bold transition-colors hover:text-seed-salad ${apptStatus(a) === "concluido" ? "text-seed-deep/40 line-through" : "text-seed-deep"}`} data-testid={`kanban-edit-${a.id.slice(0, 8)}`}>
                                  {a.title}
                                </button>
                              </div>
                              <p className="mt-1 text-[10px] font-semibold text-seed-deep/50">{fmtDay(a.date)} · {assigneeName(a.assignee)}</p>
                              {a.description && <p className="mt-0.5 truncate text-[10px] text-seed-deep/40">{a.description}</p>}
                              <div className="mt-2 flex items-center gap-1.5">
                                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${typeCls(a.type)}`}>{typeLabel(a.type)}</span>
                                {isOverdue(a) && (
                                  <span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-bold uppercase text-red-500" data-testid={`kanban-overdue-${a.id.slice(0, 8)}`}>Atrasado</span>
                                )}
                              </div>
                              <div className="mt-2 flex items-center justify-between border-t border-seed-deep/5 pt-2">
                                {ci > 0 ? (
                                  <button onClick={() => moveCard(a, columns[ci - 1].id)} aria-label={`Mover para ${columns[ci - 1].label}`} title={`Mover para ${columns[ci - 1].label}`} className="flex items-center gap-1 rounded-full bg-seed-off px-2.5 py-1 text-[10px] font-bold text-seed-deep/50 transition-colors hover:text-seed-salad" data-testid={`kanban-prev-${a.id.slice(0, 8)}`}>
                                    <ChevronLeft size={12} /> {columns[ci - 1].label}
                                  </button>
                                ) : <span />}
                                {ci < columns.length - 1 ? (
                                  <button onClick={() => moveCard(a, columns[ci + 1].id)} aria-label={`Mover para ${columns[ci + 1].label}`} title={`Mover para ${columns[ci + 1].label}`} className="flex items-center gap-1 rounded-full bg-seed-off px-2.5 py-1 text-[10px] font-bold text-seed-deep/50 transition-colors hover:text-seed-salad" data-testid={`kanban-next-${a.id.slice(0, 8)}`}>
                                    {columns[ci + 1].label} <ChevronRight size={12} />
                                  </button>
                                ) : <span />}
                              </div>
                            </div>
                          ))}
                          {colItems.length === 0 && (
                            <p className="rounded-md border-2 border-dashed border-seed-deep/10 py-6 text-center text-[10px] font-semibold text-seed-deep/30">Arraste cartões aqui</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div className="w-64 shrink-0">
                    {addingColumn ? (
                      <div className="rounded-lg bg-seed-off/60 p-3" data-testid="kanban-new-column-form">
                        <input
                          autoFocus
                          placeholder="Nome da coluna (ex.: Aguardando cliente)"
                          value={newColumn}
                          onChange={(e) => setNewColumn(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addColumn(); } }}
                          className="h-9 w-full rounded-md bg-white px-3 text-xs font-semibold text-seed-deep outline-none placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]"
                          data-testid="kanban-new-column-input"
                        />
                        <div className="mt-2 flex items-center gap-2">
                          <button onClick={addColumn} disabled={!newColumn.trim()} className="rounded-full bg-seed-deep px-4 py-1.5 text-[10px] font-bold text-seed-off disabled:opacity-50" data-testid="kanban-new-column-save">Criar coluna</button>
                          <button onClick={() => { setAddingColumn(false); setNewColumn(""); }} className="text-[10px] font-bold text-seed-deep/50 hover:text-red-500" data-testid="kanban-new-column-cancel">Cancelar</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setAddingColumn(true)} className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-seed-deep/15 py-8 font-heading text-xs font-bold text-seed-deep/40 transition-colors hover:border-seed-salad hover:text-seed-salad" data-testid="kanban-add-column">
                        <Plus size={14} /> Nova coluna
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </main>
  );
}
